import { isEmpty, omit } from "lodash"
import React, { MouseEventHandler, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row, Tab, Tabs } from "react-bootstrap"
import { useTranslation } from "react-i18next"

const AddGroupModal = ({
    show,
    loading,
    handleSubmit,
    groupDetails,
    setGroupDetails,
    checkNameValidity,
    handleClose,
    errors,
    setErrors,
    modulesAndMenus
}: {
    show: boolean
    loading: boolean
    handleSubmit: Function
    groupDetails: Record<string, any>
    setGroupDetails: Function
    checkNameValidity: Function
    handleClose: () => void
    errors: Record<string, any>
    setErrors: Function
    modulesAndMenus: Record<string, any>[]
}) => {
    const { t } = useTranslation()
    const [isDisabled, setIsDisabled] = useState(false)
    const [webMenus, setWebMenus] = useState([] as Record<string, any>[])
    const [mobileMenus, setMobileMenus] = useState([] as Record<string, any>[])
    const [tab, setTab] = useState("web-menus")

    const checkFields = () => {
        if (
            !loading &&
            groupDetails.name &&
            groupDetails.name !== "" &&
            groupDetails.desc &&
            groupDetails.desc !== "" &&
            isEmpty(errors)
        ) {
            return setIsDisabled(false)
        } else {
            return setIsDisabled(true)
        }
    }

    useEffect(() => {
        checkFields()
    }, [loading, groupDetails.name, groupDetails.desc, isEmpty(errors)])

    useEffect(() => {
        setWebMenus(
            modulesAndMenus?.filter(
                (module: Record<string, any>) =>
                    module?.Menu?.filter((menu: Record<string, any>) => !menu?.is_mobile_menu)?.length > 0
            )
        )
        setMobileMenus(
            modulesAndMenus?.filter(
                (module: Record<string, any>) =>
                    module?.Menu?.filter((menu: Record<string, any>) => menu?.is_mobile_menu)?.length > 0
            )
        )
    }, [])

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t("Add New Group")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="group-modal">
                <Form>
                    {errors["genericError"] && (
                        <div className="alert alert-danger">{errors["genericError"]}</div>
                    )}
                    <Row>
                        <Col lg={6}>
                            {/** TODO: check name validity */}
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="groupName">{t("Group_name")}</Form.Label>
                                <Form.Control
                                    id="groupName"
                                    name={"name"}
                                    onBlur={(e) => {
                                        if (
                                            e.target.value &&
                                            e.target.value !== "" &&
                                            groupDetails.id &&
                                            e.target.value !== groupDetails.name
                                        )
                                            checkNameValidity(e.target.value)
                                    }}
                                    value={groupDetails.name}
                                    onChange={(e: any) => {
                                        setGroupDetails({ ...groupDetails, [e.target.name]: e.target.value })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Group name..."
                                    isInvalid={!!errors?.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.name}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={6}></Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="groupDesc">{t("Group_desc")}</Form.Label>
                                <Form.Control
                                    id="groupDesc"
                                    name={"desc"}
                                    as="textarea"
                                    value={groupDetails.desc}
                                    onChange={(e: any) => {
                                        setGroupDetails({ ...groupDetails, [e.target.name]: e.target.value })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Group desc..."
                                    isInvalid={!!errors?.desc}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.desc}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-4 mx-1">
                        <Tabs id="menus-tab" activeKey={tab} onSelect={(k: any) => setTab(k)} className="mb-3">
                            <Tab eventKey="web-menus" className="overflow-x-hidden" title="Web">
                                <div className="scrollable">
                                    <h3 className="label mb-3">{t("Web Menus Access")}</h3>
                                    <Row>
                                        {webMenus.map((module: Record<string, any>) =>
                                            module?.Menu?.filter(
                                                (menu: Record<string, any>) => !menu.is_mobile_menu
                                            )?.length === 0 ? null : (
                                                <Col
                                                    lg={4}
                                                    className="mb-2"
                                                    key={`${module.id}_${module.name}`}>
                                                    {module?.name}
                                                    {module?.Menu?.filter(
                                                        (menu: Record<string, any>) => !menu.is_mobile_menu
                                                    )?.map((menu: Record<string, any>) => (
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="my-2"
                                                            label={menu.name}
                                                            value={menu.id}
                                                            defaultChecked={
                                                                groupDetails.id &&
                                                                groupDetails?.menus?.includes(menu.id)
                                                            }
                                                            onChange={(e: any) => {
                                                                if (e.target.checked) {
                                                                    setGroupDetails({
                                                                        ...groupDetails,
                                                                        menus: [
                                                                            ...(groupDetails.menus || []),
                                                                            Number(e.target.value)
                                                                        ]
                                                                    })
                                                                } else {
                                                                    if (
                                                                        groupDetails?.menus?.includes(menu.id)
                                                                    ) {
                                                                        setGroupDetails({
                                                                            ...groupDetails,
                                                                            menus: (
                                                                                groupDetails?.menus || []
                                                                            )?.filter(
                                                                                (m: number) => m !== menu.id
                                                                            )
                                                                        })
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                </div>
                            </Tab>
                            <Tab eventKey="mobile-menus" className="overflow-x-hidden" title="Mobile">
                                <div className="scrollable">
                                    <h3 className="label mb-3">{t("Mobile Menus Access")}</h3>
                                    <Row>
                                        {mobileMenus.map((module: Record<string, any>) =>
                                            module?.Menu?.filter(
                                                (menu: Record<string, any>) => !menu.is_mobile_menu
                                            )?.length === 0 ? null : (
                                                <Col
                                                    lg={4}
                                                    className="mb-2"
                                                    key={`mobile_${module.id}_${module.name}`}>
                                                    {module?.name}
                                                    {module?.Menu?.filter(
                                                        (menu: Record<string, any>) => menu.is_mobile_menu
                                                    )?.map((menu: Record<string, any>) => (
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="my-2"
                                                            label={menu.name}
                                                            value={menu.id}
                                                            defaultChecked={
                                                                groupDetails.id &&
                                                                groupDetails?.mobileMenus?.includes(menu.id)
                                                            }
                                                            onChange={(e: any) => {
                                                                if (e.target.checked) {
                                                                    setGroupDetails({
                                                                        ...groupDetails,
                                                                        mobileMenus: [
                                                                            ...(groupDetails.mobileMenus || []),
                                                                            Number(e.target.value)
                                                                        ]
                                                                    })
                                                                } else {
                                                                    if (
                                                                        groupDetails?.mobileMenus?.includes(
                                                                            menu.id
                                                                        )
                                                                    ) {
                                                                        setGroupDetails({
                                                                            ...groupDetails,
                                                                            mobileMenus: (
                                                                                groupDetails?.mobileMenus || []
                                                                            )?.filter(
                                                                                (m: number) => m !== menu.id
                                                                            )
                                                                        })
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                </div>
                            </Tab>
                        </Tabs>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose as MouseEventHandler<HTMLButtonElement>}>
                    {t("Close")}
                </Button>
                <Button
                    disabled={isDisabled}
                    onClick={() => handleSubmit(groupDetails)}
                    className="btn btn-primary"
                    type="submit">
                    {loading ? (
                        <div className="spinner-border text-light" role="status"></div>
                    ) : groupDetails?.id ? (
                        t("Update")
                    ) : (
                        t("Submit")
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AddGroupModal
